<template>
  <h1>Aprobador de solicitudes</h1>
  <p class="mt-2 text-800 font-medium">
    Lista de solicitudes que han sido enviadas a tu persona para su aprobación.
  </p>
  <Toolbar class="w-11 mt-3">
    <template #start>
      <Button
        v-if="thereAreBackupRequests"
        @click="changeRequestBackUp"
        class="mr-2 text-sm font-light"
      >
        <i class="pi pi-eye mr-2" />
        <label v-if="!requestAreBackup">Solicitudes Backup</label>
        <label v-else="requestAreBackup">Pendientes</label>
      </Button>
      <Button
        @click="changeRequestHistoric"
        class="font-medium cursor-pointer"
        severity="secondary"
      >
        <i v-if="!requestsAreHistoric" class="pi pi-history mr-2" />
        <i v-else class="pi pi-fw pi-eye mr-2" />
        <label class="cursor-pointer" v-if="!requestsAreHistoric"
          >Ver historial</label
        >
        <label class="cursor-pointer" v-else="requestsAreHistoric"
          >Ver pendientes</label
        >
      </Button>
    </template>
    <template #end>
      <Button
        label="Procesar selección"
        icon="pi pi-check"
        severity="primary"
        @click="processMultipleRequests"
        :disabled="
          !(selectedRequests && selectedRequests.length > 0) ||
          requestsAreHistoric
        "
        class="text-sm font-light"
      />
    </template>
  </Toolbar>
  <DataTable
    v-model:selection="selectedRequests"
    :value="requests"
    dataKey="id"
    :loading="isLoading"
    v-model:filters="filters"
    filter-display="row"
    sortField="createdAt"
    :sortOrder="-1"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    tableStyle="min-width: 60rem"
    currentPageReportTemplate="{first} a {last} de {totalRecords}"
    class="tabla mb-3"
    paginator
    :rows="10"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    :globalFilterFields="['employee', 'type']"
    @filter="onFilter($event)"
  >
    <template #header>
      <div class="flex justify-content-end">
        <Button
          icon="pi pi-file-excel"
          label="Exportar"
          @click="exportCSV()"
          class="text-sm font-light bg-green-100 border-green-100 text-green-600 mr-3"
        />
        <span class="p-input-icon-left">
          <i class="pi pi-search" />
          <InputText v-model="filters['global'].value" placeholder="Buscar" />
        </span>
      </div>
    </template>
    <Column
      selectionMode="multiple"
      headerStyle="width: 3rem"
      v-if="!requestsAreHistoric"
    ></Column>
    <Column
      field="employee"
      header="Empleado"
      sortable
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '14rem' }"
      style="min-width: 14rem"
      filter
    >
      <template #body="{ data, field }">
        <span class="font-medium text-800">{{ data[field] }}</span>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="employees"
          optionLabel=""
          placeholder=""
          class="p-column-filter"
          style="min-width: 14rem"
          :maxSelectedLabels="1"
        >
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <span>{{ slotProps.option }}</span>
            </div>
          </template>
        </MultiSelect>
      </template>
    </Column>
    <Column
      header="Tipo de solicitud"
      field="type"
      sortable
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '14rem' }"
      filter
    >
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="types"
          optionLabel=""
          placeholder=""
          class="p-column-filter"
          style="min-width: 14rem"
          :maxSelectedLabels="1"
        >
          <template #option="slotProps">
            <span>{{ formatRequestType(slotProps.option) }}</span>
          </template>
        </MultiSelect>
      </template>
    </Column>
    <Column header="Cantidad" field="quantity" sortable>
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column header="Fecha inicio" field="dateInit" sortable>
      <template #body="{ data, field }">
        <span v-basic-date="data[field]" v-if="data[field]"></span>
      </template>
    </Column>
    <Column header="Fecha final" field="dateEnd" sortable>
      <template #body="{ data, field }">
        <span v-basic-date="data[field]" v-if="data[field]"></span>
      </template>
    </Column>
    <Column header="Aprobadores">
      <template #body="{ data }">
        {{ data.approvedCount }} / {{ data.approversCount }}
      </template>
    </Column>
    <Column field="globalStatus" header="Estado" style="width: 20%" sortable>
      <template #body="{ data, field }">
        <Tag
          :value="formatRequestState(data[field])"
          :severity="getStatusLabel(data[field])"
          class=""
        ></Tag>
      </template>
    </Column>
    <Column field="createdAt" header="Fecha de creación" sortable>
      <template #body="{ data, field }">
        <span v-local-date="data[field]"></span>
      </template>
    </Column>
    <Column header="Acciones" field="steps">
      <template #body="{ data }">
        <div class="flex flex-justify-end">
          <Button
            v-if="data.globalStatus === 'PENDING' && !requestsAreHistoric"
            v-tooltip.bottom="'Procesar solicitud'"
            class="h-2rem hover:text-blue-800 mr-2"
            severity="secondary"
            icon="pi pi-check-circle"
            @click="processSingleRequest(data)"
          ></Button>
          <Button
            v-tooltip.bottom="'Ver detalles'"
            icon="pi pi-eye"
            class="h-2rem hover:text-blue-800"
            severity="primary"
            @click="goToDetail(data['id'])"
          />
        </div>
      </template>
    </Column>
  </DataTable>
  <Dialog v-model:visible="approvalDialogVisibility">
    <template #header>
      <h3>Procesar solicitud</h3>
    </template>
    <template #default>
      <Textarea
        v-model="resolutionComment"
        rows="5"
        cols="30"
        autoResize
        placeholder="Escribe un comentario..."
      ></Textarea>
    </template>
    <template #footer>
      <Button
        class="text-sm font-light h-2rem"
        severity="secondary"
        @click="approvalDialogVisibility = false"
      >
        <label class="text-sm font-medium cursor-pointer">Cancelar</label>
      </Button>
      <Button
        class="text-sm font-light h-2rem"
        severity="danger"
        @click="onResolutionRequest('REJECTED')"
        :loading="isProcessLoading"
        :disabled="isProcessLoading"
      >
        <label class="text-sm font-medium cursor-pointer">Rechazar</label>
      </Button>
      <Button
        class="text-sm font-light h-2rem"
        severity="primary"
        @click="onResolutionRequest('APPROVED')"
        :loading="isProcessLoading"
        :disabled="isProcessLoading"
      >
        <label class="text-sm font-medium cursor-pointer">Aprobar</label>
      </Button>
    </template>
  </Dialog>
</template>
<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import formatRequestState from '@/shared/utils/formatRequestState'
  import {
    createRequestResolution,
    getAllPendingRequests,
    getAllRequests,
    getPendingBackupRequests,
  } from '../services/ApprovalRequestService'
  import Tag from 'primevue/tag'
  import Toolbar from 'primevue/toolbar'
  import useNotifications from '@/store/notification'
  import { FilterMatchMode } from 'primevue/api'
  import router from '@/router'
  import { useRequestStore } from '@/store/requestBackup'
  import { ApprovalRequest } from '../models/ApprovalRequest'
  import { formatRequestType } from '@/shared/utils/formatRequestType'
  import { exportExcel } from '@/shared/utils/reports/requestsReportsXlsx'

  const requests = ref<ApprovalRequest[]>()

  const approvalDialogVisibility = ref(false)
  const singleRequestProcessing = ref<ApprovalRequest>()
  const multipleRequestsProcessing = ref<ApprovalRequest[]>()
  const isProcessLoading = ref(false)
  const isLoading = ref(false)

  const resolutionComment = ref('')
  const notificationStore = useNotifications()
  const requestStore = useRequestStore()
  const requestAreBackup = ref(false)
  const requestsAreHistoric = ref(false)
  const selectedRequests = ref<ApprovalRequest[]>()
  const thereAreBackupRequests = ref(false)
  const filteredRequests = ref<ApprovalRequest[]>()
  const employees = ref()
  const types = ref()
  onMounted(async () => {
    await refreshRequests()
  })

  const changeRequestHistoric = async () => {
    requestsAreHistoric.value = !requestsAreHistoric.value
    isLoading.value = true
    if (requestsAreHistoric.value) {
      const response = await getAllRequests()
      requests.value = response
      filters.value = {
        ...filters.value,
        globalStatus: { value: '', matchMode: FilterMatchMode.EQUALS },
      }
      setFilters(requests)
    } else {
      filters.value = {
        ...filters.value,
        globalStatus: { value: 'PENDING', matchMode: FilterMatchMode.EQUALS },
      }
      await refreshRequests()
    }
    localStorage.setItem(
      'requestAreHistoric',
      String(requestsAreHistoric.value)
    )
    isLoading.value = false
  }

  const changeRequestBackUp = async () => {
    requestAreBackup.value = !requestAreBackup.value
    isLoading.value = true
    if (requestAreBackup.value) {
      const response = await getPendingBackupRequests()
      requests.value = response
    } else {
      await refreshRequests()
    }

    isLoading.value = false
  }

  const refreshRequests = async () => {
    isLoading.value = true
    requestAreBackup.value = false
    requestsAreHistoric.value = false
    const [allPendingResponse, pendingBackupResponse] = await Promise.all([
      getAllPendingRequests(),
      getPendingBackupRequests(),
    ])

    if (!allPendingResponse || !pendingBackupResponse) {
      isLoading.value = false
      return
    }

    requests.value = allPendingResponse
    setFilters(requests)
    pendingBackupResponse.length > 0
      ? (thereAreBackupRequests.value = true)
      : (thereAreBackupRequests.value = false)
    isLoading.value = false
  }

  const processSingleRequest = async (request: ApprovalRequest) => {
    approvalDialogVisibility.value = true
    singleRequestProcessing.value = request
  }

  const processMultipleRequests = async () => {
    approvalDialogVisibility.value = true
    multipleRequestsProcessing.value = selectedRequests.value
  }

  const onResolutionRequest = async (state: string) => {
    isProcessLoading.value = true
    let response: string = ''

    if (
      multipleRequestsProcessing.value &&
      multipleRequestsProcessing.value?.length > 0 &&
      singleRequestProcessing.value === undefined
    ) {
      response = await createRequestResolution(
        {
          resolutions: multipleRequestsProcessing.value.map((request) => {
            return {
              approvalFlowRequestId: request.id || '',
              resolutionComment: resolutionComment.value,
              resolutionMethod: 'RequestModule',
              resolutionState: state,
            }
          }),
        },
        requestAreBackup.value
      )
    } else {
      response = await createRequestResolution(
        {
          resolutions: [
            {
              approvalFlowRequestId: singleRequestProcessing.value?.id || '',
              resolutionComment: resolutionComment.value,
              resolutionMethod: 'RequestModule',
              resolutionState: state,
            },
          ],
        },
        requestAreBackup.value
      )
    }

    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Solicitud(es) procesada(s) exitosamente`,
      })
    }

    isProcessLoading.value = false
    approvalDialogVisibility.value = false
    singleRequestProcessing.value = undefined
    multipleRequestsProcessing.value = undefined
    resolutionComment.value = ''
    selectedRequests.value = []
    await refreshRequests()
  }

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    globalStatus: { value: 'PENDING', matchMode: FilterMatchMode.EQUALS },
    employee: { value: null, matchMode: FilterMatchMode.IN },
    type: { value: null, matchMode: FilterMatchMode.IN },
  })

  const goToDetail = (id: string) => {
    requestStore.setId(id)
    requestStore.setRequest(requestAreBackup.value)
    router.push('/requestApprover/' + id)
  }

  const onFilter = (event: any) => {
    const { filteredValue } = event
    const filteredValueCasted = filteredValue as ApprovalRequest[]
    filteredRequests.value =
      filteredValueCasted.length > 0 ? filteredValueCasted : requests.value
  }

  const setFilters = (requests: any) => {
    employees.value = requests.value?.reduce((acc: any, curr: any) => {
      if (!acc.find((e: any) => e === curr.employee)) {
        acc.push(curr.employee)
      }
      return acc
    }, [])
    types.value = requests.value?.reduce((acc: any, curr: any) => {
      if (!acc.find((e: any) => e === curr.type)) {
        acc.push(curr.type)
      }
      return acc
    }, [])
  }

  const exportCSV = () => {
    if (!filteredRequests.value) return
    exportExcel(filteredRequests.value)
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success'
      case 'PENDING':
        return 'warning'
      case 'REJECTED':
        return 'danger'
      default:
        return 'warning'
    }
  }
</script>
